// src/components/orders/MobileOrdersView.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  Button
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CalendarToday from '@mui/icons-material/CalendarToday';
import ShoppingBag from '@mui/icons-material/ShoppingBag';
import AttachMoney from '@mui/icons-material/AttachMoney';
import Info from '@mui/icons-material/Info';

const MobileOrdersView = ({ orders, getOrderItemsSummary, getStatusColor, getStatusLabel }) => {
  const navigate = useNavigate();

  const MobileOrderCard = ({ order }) => (
    <Card 
      sx={{ 
        mb: 2, 
        borderRadius: 2,
        boxShadow: 2,
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)',
          transition: 'all 0.3s ease'
        }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Order Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            #{order.id.slice(0, 8)}
          </Typography>
          <Chip
            icon={
              order.status === 'pending' ? <AccessTimeIcon sx={{ fontSize: 16 }} /> :
              order.status === 'confirmed' ? <ThumbUpIcon sx={{ fontSize: 16 }} /> :
              order.status === 'shipped' ? <LocalShippingIcon sx={{ fontSize: 16 }} /> :
              order.status === 'delivered' ? <CheckCircleOutlineIcon sx={{ fontSize: 16 }} /> :
              order.status === 'cancelled' ? <HighlightOffIcon sx={{ fontSize: 16 }} /> :
              null
            }
            label={getStatusLabel(order.status)}
            color={getStatusColor(order.status)}
            size="small"
            sx={{ 
              fontWeight: 'bold',
              minWidth: '120px',
              '& .MuiChip-icon': {
                color: 'inherit'
              }
            }}
          />
        </Box>

        {/* Order Details */}
        <Stack spacing={1.5}>
          {/* Date */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarToday sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold'}}>
              {new Date(order.created_at).toLocaleDateString('ar-EG', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
          </Box>

          {/* Products */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <ShoppingBag sx={{ fontSize: 18, color: 'text.secondary', mt: 0.2 }} />
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                flex: 1,
                wordBreak: 'break-word',
                lineHeight: 1.4,
                fontWeight: 'bold'
              }}
            >
              {getOrderItemsSummary(order)}
            </Typography>
          </Box>

          {/* Amount */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachMoney sx={{ fontSize: 18, color: 'success.main' }} />
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'success.main' }}>
              {order.total_amount.toFixed(2)} جنيه
            </Typography>
          </Box>
        </Stack>

        {/* Action Button */}
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<Info />}
            onClick={() => navigate(`/order/${order.id}`)}
            sx={{
              borderRadius: 2,
              fontWeight: 'bold',
              '& .MuiButton-startIcon': {
                marginRight: '4px',
                marginLeft: 1
              }
            }}
          >
            عرض التفاصيل
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {orders.map((order) => (
        <MobileOrderCard key={order.id} order={order} />
      ))}
    </Box>
  );
};

export default MobileOrdersView;